import React, { useState } from 'react'
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Icon,
  Input,
  Popconfirm,
  Row,
  Select,
  Table,
  Avatar
} from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useDeepEffect } from '../../utils/hooks'
import {
  getRecycleBarcodeProduct,
  getBarcodeRecycleItemByBarcodeProductId,
  updateBarcodeRecycleItem,
  deleteRecycleBarcodeProduct
} from '../../modules/barcodeProduct'
import { getMaterials } from '../../modules/material'
import { isEmpty, isNil } from 'lodash'
import EditRecycleBarcodeProduct from './EditRecycleBarcodeProduct'

const initialOptions = {
  size: 20,
  page: 0,
  desc: true,
  keyword: undefined,
  createdBy: undefined,
  materials: []
}

const RecycleBarcodeProduct = (props) => {
  const { form } = props
  const dispatch = useDispatch()
  const [options, setOptions] = useState(initialOptions)
  const [openEditModal, setOpenEditModal] = useState(false)
  const [formData, setFormData] = useState({})
  const [materialAllList, setMaterialAllList] = useState([])

  const { recycleBarcodeProduct, isLoading } = useSelector(
    (state) => ({
      recycleBarcodeProduct: state.barcodeProduct.recycleBarcodeProduct,
      isLoading: state.barcodeProduct.isLoading
    }),
    []
  )

  const { materials } = useSelector(
    (state) => ({
      materials: state.material.materials
    }),
    []
  )

  async function fetchData() {
    await dispatch(getRecycleBarcodeProduct(options))
    await dispatch(getMaterials())
  }

  useDeepEffect(() => {
    fetchData()
  }, [])

  useDeepEffect(() => {
    if (!isNil(materials)) {
      const materialList = materials.map((material) => {
        return material.materialList
      })

      const materialAllList = []
      materialList.forEach((material) => {
        materialAllList.push(...material)
      })
      setMaterialAllList(materialAllList)
    }
  }, [materials])

  const columns = [
    {
      title: 'ชื่อ',
      dataIndex: 'name',
      key: 'name',
      render: (name, row) => (
        <div>
          <Avatar
            key={row.productUrl}
            src={row.productUrl}
            size={43}
            style={{ marginRight: 15 }}
          />
          {`${row.productBrand} ${row.productName} `}
        </div>
      )
    },
    {
      title: 'จำนวนชิ้น',
      key: 'quantity',
      width: 90,
      render: (name, row) => <div>{`${row.itemNumber} `}</div>
    },
    {
      title: 'วัสดุที่ได้',
      key: 'material',
      dataIndex: 'material',
      render: (name, row) => (
        <div>
          {row.materialUrls && !isEmpty(row.materialUrls) ? (
            [...new Set(row.materialUrls)].map((material, index) => {
              return (
                <Avatar
                  key={material + index}
                  src={material}
                  size={43}
                  style={{ marginRight: 5 }}
                />
              )
            })
          ) : (
            <span>-</span>
          )}
        </div>
      )
    },
    {
      title: 'คะแนน',
      key: 'score',
      width: 200,
      render: (text, record) => (
        <Row type="flex" gutter={8}>
          <Col>
            <Row type="flex">
              <Col>
                <span style={{ fontSize: '18px' }}>{record.goodReaction}</span>
              </Col>
              <Col>
                <Icon
                  type="check-circle"
                  style={{ color: '#A4D55D', fontSize: '24px' }}
                />
              </Col>
            </Row>
          </Col>
          <Col>
            <Row type="flex">
              <Col>
                <span style={{ fontSize: '18px' }}>
                  {record.uncertainReaction}
                </span>
              </Col>
              <Col>
                <Icon type="minus-circle" style={{ fontSize: '24px' }} />
              </Col>
            </Row>
          </Col>
          <Col>
            <Row type="flex">
              <Col>
                <span style={{ fontSize: '18px' }}>{record.badReaction}</span>
              </Col>
              <Col>
                <Icon
                  type="close-circle"
                  style={{ color: '#FF6C6C', fontSize: '24px' }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      )
    },
    {
      title: 'ผู้คัดแยก',
      key: 'createdBy',
      dataIndex: 'createdBy'
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      width: 175,
      fixed: 'right',
      render: (text, record) => (
        <span>
          <Button
            type="primary"
            size="small"
            shape="circle"
            icon="edit"
            onClick={() => {
              openDialog(record.barcodeRecycleId)
            }}
          />
          <Divider type="vertical" />
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => onDelete(record.barcodeRecycleId)}>
            <Button type="danger" size="small" shape="circle" icon="delete" />
          </Popconfirm>
        </span>
      )
    }
  ]

  const onCloseModal = (key) => {
    setOpenEditModal(false)
    setFormData({})
  }

  const onDelete = async (id) => {
    await dispatch(deleteRecycleBarcodeProduct(id))
    await dispatch(getRecycleBarcodeProduct(options))
  }

  const openDialog = async (id) => {
    // Set data id
    await dispatch(getBarcodeRecycleItemByBarcodeProductId(id))
    await setOpenEditModal(true)
    await setFormData({
      id
    })
  }

  async function onShowSizeChange(current, pageSize) {
    options.size = pageSize
    setOptions(options)
    await dispatch(getRecycleBarcodeProduct(options))
  }

  async function onChange(page) {
    options.page = page - 1
    setOptions(options)
    await dispatch(getRecycleBarcodeProduct(options))
  }

  const reset = async (e) => {
    e.preventDefault()
    options.keyword = undefined
    options.createdBy = undefined
    options.barcode = undefined
    options.materials = []
    await dispatch(getRecycleBarcodeProduct(options))
    await form.resetFields()
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    form.validateFields(async (err, values) => {
      if (!err) {
        if (values.keyword) {
          options.keyword = values.keyword
          options.page = 0
        } else {
          options.keyword = ''
          options.page = 0
        }

        if (values.createdBy) {
          options.createdBy = values.createdBy
          options.page = 0
        } else {
          options.createdBy = ''
          options.page = 0
        }

        if (values.barcode) {
          options.barcode = values.barcode
          options.page = 0
        } else {
          options.barcode = ''
          options.page = 0
        }

        if (values.materials) {
          const materialIds = getMaterialIds(values.materials)
          options.materials = materialIds
        }
        setOptions(options)
        dispatch(getRecycleBarcodeProduct(options))
      }
    })
  }

  const getMaterialIds = (materials) => {
    const ids = []
    materials.forEach((material) => {
      const id = materialAllList.filter((m) => m.name === material)
      if (!isEmpty(id)) ids.push(id[0].id)
    })
    return ids
  }

  const getMaterialList = () => {
    if (!isNil(materials) && !isNil(materialAllList)) {
      return materialAllList.map((material) => {
        return (
          <Select.Option key={material.id} value={material.name}>
            {material.name}
          </Select.Option>
        )
      })
    }
  }

  const onOk = async (data) => {
    await dispatch(updateBarcodeRecycleItem(data))
    await setOpenEditModal(false)
    setFormData({})
    await dispatch(getRecycleBarcodeProduct(options))
  }

  return (
    <Card title="Recycle Barcode Product">
      <Form layout="vertical" onSubmit={handleSubmit}>
        <Row type="flex" gutter={8}>
          <Col>
            <Form.Item label="ค้นหาวิธีคัดแยกตามชื่อสินค้า">
              {form.getFieldDecorator('keyword')(
                <Input placeholder="ชื่อสินค้าหรือยี่ห้อสินค้า" />
              )}
            </Form.Item>
          </Col>
          <Col>
            <Form.Item label="ค้นหาวิธีคัดแยกตามผู้เพิ่มวิธีคัดแยก">
              {form.getFieldDecorator('createdBy')(
                <Input placeholder="ชื่อผู้เพิ่มวิธีคัดแยก" />
              )}
            </Form.Item>
          </Col>
          <Col>
            <Form.Item label="ค้นหาวิธีคัดแยกตามบาร์โค้ด">
              {form.getFieldDecorator('barcode')(
                <Input placeholder="Barcode" />
              )}
            </Form.Item>
          </Col>
          <Col>
            <Form.Item label="ค้นหาวิธีคัดแยกตามวัสดุที่ได้">
              {materials &&
                form.getFieldDecorator('materials')(
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Please select">
                    {getMaterialList()}
                  </Select>
                )}
            </Form.Item>
          </Col>
          <Col>
            <Form.Item style={{ marginTop: 30 }}>
              <Button type="primary" htmlType="submit">
                ค้นหา
              </Button>
            </Form.Item>
          </Col>
          <Col>
            <Form.Item style={{ marginTop: 30 }}>
              <Button onClick={(e) => reset(e)}>รีเซ็ต</Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <br />
      <Table
        scroll={{ x: 1200 }}
        loading={isLoading}
        bordered
        rowKey={(record) => record.id}
        dataSource={recycleBarcodeProduct.content}
        columns={columns}
        pagination={{
          current: recycleBarcodeProduct.pageable.pageNumber + 1,
          pageSize: recycleBarcodeProduct.size,
          pageSizeOptions: ['10', '20', '50', '100'],
          showSizeChanger: true,
          onShowSizeChange: onShowSizeChange,
          onChange: onChange,
          total: recycleBarcodeProduct.totalElements
        }}
      />
      <EditRecycleBarcodeProduct
        visible={openEditModal}
        formData={formData}
        onOk={onOk}
        onCancel={onCloseModal}
      />
    </Card>
  )
}

const WrappedRecycleBarcodeProduct = Form.create({
  name: 'RecycleBarcodeProduct'
})(RecycleBarcodeProduct)

export default WrappedRecycleBarcodeProduct



// WEBPACK FOOTER //
// ./src/pages/RecycleBarcodeProduct/index.js